import { useState } from 'react';
import {
  Box, Typography, FormControl, Select, MenuItem, Button,
  Chip, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';

// Remove the booksAPI import to avoid circular dependency
// import { booksAPI } from '../../services/api';

const CategoryManager = ({ 
  entityId, 
  entityType = 'book', 
  categoryIds = [], 
  categories = [], 
  onChange, 
  setSnackbar,
  // Pass API functions as props instead of importing directly
  addCategories,
  removeCategories
}) => {
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);

  // Get available categories (not already added to the entity)
  // Filter out inactive categories and sort by sortOrder
  const getAvailableCategories = () => {
    const currentCategoryIds = categoryIds || [];
    return categories
      .filter(category => !currentCategoryIds.includes(category.id) && category.isActive)
      .sort((a, b) => {
        // First by sortOrder (if present)
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        // Then alphabetically by name
        return a.name.localeCompare(b.name);
      });
  };

  // Function to handle adding a single category
  const handleAddCategory = async () => {
    if (!entityId || !selectedCategory) return;
    
    try {
      setAddCategoryLoading(true);
      
      // Use the API function passed as prop
      if (addCategories) {
        await addCategories(entityId, [selectedCategory]);
      }
      
      // Update local state to reflect the change
      if (onChange) {
        const updatedCategoryIds = [...(categoryIds || []), selectedCategory];
        onChange(updatedCategoryIds);
      }
      
      // Reset the dropdown
      setSelectedCategory('');
      setCategoryDropdownOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Category added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding category:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add category',
        severity: 'error'
      });
    } finally {
      setAddCategoryLoading(false);
    }
  };
  
  // Function to handle removing a single category
  const handleRemoveCategory = async (categoryId) => {
    if (!entityId) return;
    
    try {
      // Use the API function passed as prop
      if (removeCategories) {
        await removeCategories(entityId, [categoryId]);
      }
      
      // Update local state to reflect the change
      if (onChange) {
        const updatedCategoryIds = (categoryIds || []).filter(id => id !== categoryId);
        onChange(updatedCategoryIds);
      }
      
      setSnackbar({
        open: true,
        message: 'Category removed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing category:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove category',
        severity: 'error'
      });
    }
  };

  // Function to render category chips
  const renderCategoryChips = () => {
    if (!categoryIds || !categories.length) {
      return <Typography variant="body2">None</Typography>;
    }
    
    // Get full category objects for the IDs, filtering out any that don't exist
    // or are not active, and sort them by sortOrder
    const categoryObjects = categoryIds
      .map(categoryId => categories.find(c => c.id === categoryId))
      .filter(category => category && category.isActive)
      .sort((a, b) => {
        // First by sortOrder (if present)
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        // Then alphabetically by name
        return a.name.localeCompare(b.name);
      });
    
    if (categoryObjects.length === 0) {
      return <Typography variant="body2">None</Typography>;
    }
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {categoryObjects.map(category => (
          <Chip 
            key={category.id}
            label={category.name}
            size="small"
            onDelete={() => handleRemoveCategory(category.id)}
            deleteIcon={<CancelIcon fontSize="small" />}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Categories
      </Typography>
      
      {/* Display category chips with delete option */}
      {renderCategoryChips()}
      
      {/* Add category dropdown */}
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start' }}>
        {categoryDropdownOpen ? (
          <>
            <FormControl size="small" sx={{ minWidth: 200, mr: 1 }}>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="" disabled>
                  <em>Select a category</em>
                </MenuItem>
                {getAvailableCategories().map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              onClick={handleAddCategory}
              disabled={!selectedCategory || addCategoryLoading}
              startIcon={addCategoryLoading ? <CircularProgress size={16} /> : <AddIcon />}
            >
              Add
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setCategoryDropdownOpen(false)}
              sx={{ ml: 1 }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCategoryDropdownOpen(true)}
            disabled={getAvailableCategories().length === 0}
          >
            Add Category
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CategoryManager;
